import { Duration } from 'dayjs/plugin/duration.js';
import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { DurationRange } from '../../models/serializable/duration-range.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Component } from '../core/component.js';

export class ServiceDurationComponent extends Component {
  static readonly TYPE_ID: string = '6ba14b3af22141f2b6561253ab39fd2d';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly durationRange: DurationRange,
  ) {
    super();
  }

  get minDuration(): Duration {
    return this.durationRange.from;
  }

  get maxDuration(): Duration {
    return this.durationRange.to;
  }

  get $typeId(): string {
    return ServiceDurationComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(ServiceDurationComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static fromDuration(duration: Duration): ServiceDurationComponent {
    return new ServiceDurationComponent(DurationRange.exactly(duration));
  }

  static fromMap(context: SerializationContext, map: SerializedData): ServiceDurationComponent {
    return new ServiceDurationComponent(
        context.fromData<DurationRange>(MapUtils.get<SerializedData>(map, 'durationRange')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'durationRange': context.toData(this.durationRange),
    };
  }

  clone(): this {
    return new ServiceDurationComponent(
        this.durationRange.clone(),
    ) as this;
  }

  isSame(other: ServiceDurationComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.durationRange, this.durationRange);
  }
}

export class ServiceDurationComponentBuilder extends DataModelBuilder<ServiceDurationComponent> {
  build(context: SerializationContext, map: SerializedData): ServiceDurationComponent {
    return ServiceDurationComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ServiceDurationComponent.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(ServiceDurationComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // / In this version `duration` of type [Duration] was changed to `durationRange` of type [DurationRange].
    if(version < 2) {
      const duration = context.decodeDuration(MapUtils.get<number>(map, 'duration'));

      MapUtils.set(map, 'durationRange', context.toData(DurationRange.exactly(duration)));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
